import React, { createContext, useContext, useState, useEffect } from 'react';
import { API } from '../api';
import { decodeJwtPayload, normalizeAuthUser } from '../utils/auth';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const readStoredUser = () => {
    const storedUser = localStorage.getItem('user');
    if (!storedUser) {
      return null;
    }

    try {
      return JSON.parse(storedUser);
    } catch (error) {
      return null;
    }
  };

  const persistUser = (nextUser) => {
    if (!nextUser) {
      return;
    }

    localStorage.setItem('user', JSON.stringify(nextUser));
    setUser(nextUser);
  };

  useEffect(() => {
    const checkAuth = async () => {
      const accessToken = localStorage.getItem('accessToken');
      const refreshToken = localStorage.getItem('refreshToken');
      const storedUser = readStoredUser();

      if (accessToken) {
        const tokenPayload = decodeJwtPayload(accessToken);
        const nextUser = normalizeAuthUser(storedUser || {}, tokenPayload);
        if (nextUser.username || nextUser.displayName || storedUser) {
          persistUser(nextUser);
        }
        setIsAuthenticated(true);
      } else if (refreshToken) {
        try {
          const result = await API.auth.refreshToken(refreshToken);
          if (result.success) {
            const newAccessToken = result.data.accessToken || result.data.access_token || result.data.token;
            if (newAccessToken) {
              localStorage.setItem('accessToken', newAccessToken);
            }

            const tokenPayload = decodeJwtPayload(newAccessToken);
            const nextUser = normalizeAuthUser(storedUser || {}, tokenPayload);
            if (nextUser.username || nextUser.displayName || storedUser) {
              persistUser(nextUser);
            }
            setIsAuthenticated(true);
          } else {
            logout();
          }
        } catch (error) {
          logout();
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, []);

  const login = async (username, password) => {
    try {
      const result = await API.auth.login(username, password);
      if (result.success) {
        const accessToken = result.data.accessToken || result.data.access_token || result.data.token;
        const refreshToken = result.data.refreshToken || result.data.refresh_token;
        const tokenPayload = decodeJwtPayload(accessToken);
        const nextUser = normalizeAuthUser(
          {
            ...result.data.user,
            username: result.data.user?.username || username,
          },
          tokenPayload
        );

        if (accessToken) {
          localStorage.setItem('accessToken', accessToken);
        }
        if (refreshToken) {
          localStorage.setItem('refreshToken', refreshToken);
        }

        persistUser(nextUser);
        setIsAuthenticated(true);
        return { success: true };
      }
      return result;
    } catch (error) {
      return { success: false, error: 'Ошибка входа' };
    }
  };

  const logout = async () => {
    try {
      await API.auth.logout();
    } finally {
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      setUser(null);
      setIsAuthenticated(false);
    }
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, loading, isAuthenticated }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
